import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { coursesAccessGuardQueryKeys, type CourseAccessData } from '@courses/routing-layer/services/courses-access-guard.service'
import type { ICourseTemplate } from '@courses/types-layer/types/course-template'
import type { ICompleteProduct, IProductDetail, ProductValidity } from '@courses/types-layer/types/product'
import { useProductUIStore } from './product.ui-store'
import { ProductOfferService } from '../services/product-offer.service'
import { ProductLearnerService } from '../services/product-learner.service'
import { ProductService } from '../services/product.service'
import type { CompleteProductQueryOptions } from '../types/product-query.types'

export const useProductQueryStore = (locationId: MaybeRefOrGetter<string | undefined>) => {
  const service = new ProductService()
  const offerService = new ProductOfferService()
  const learnerService = new ProductLearnerService()
  const queryClient = useQueryClient()
  const { store: authStore } = useAuth()
  const { productLocationId } = useProductUIStore()

  const queryKeys = {
    all: (locId?: string) => ['product', locId] as const,
    detail: (productId: string, locId?: string) => ['product', locId, 'detail', productId] as const,
    validity: (productId: string, locId?: string) => ['product', locId, 'validity', productId] as const,
    offer: (productId: string, locId?: string) => ['product', locId, 'offer', productId] as const,
    pinned: (locId?: string) => ['product', locId, 'pinned'] as const,
    customizationSettings: (productId: string, locId?: string) => ['product', locId, 'customization-settings', productId] as const,
    templateByProductId: (productId: string, locId?: string) => ['product', locId, 'template-by-product-id', productId] as const,
    themeExists: (productId: string, locId?: string) => ['product', locId, 'theme-exists', productId] as const,
    templateById: (templateId: string, productId: string, locId?: string) =>
      ['product', locId, 'template-by-id', templateId, productId] as const,
    template: (templateId: string, locId?: string) => ['product', locId, 'template', templateId] as const,
    resolve: (productId: string, locId?: string) => ['product', 'resolve', locId, productId] as const,
    completeDetail: (productId: string, locId?: string) => ['product', locId, 'complete-detail', productId] as const,
  }

  const fetchProductQuery = (productId: MaybeRefOrGetter<string>) => {
    return useQuery<IProductDetail, Error>({
      queryKey: computed(() => queryKeys.detail(toValue(productId), toValue(locationId))),
      queryFn: async () => {
        const product = await service.findById(toValue(productId), toValue(locationId))
        return product
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && !!toValue(productId)),
    })
  }

  const fetchPinnedProductsQuery = () => {
    return useQuery<IProductDetail[], Error>({
      queryKey: computed(() => queryKeys.pinned(toValue(locationId))),
      queryFn: async () => {
        const products = await service.pinnedProducts()
        return products
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => process.client && !!toValue(locationId) && authStore.isLoggedIn),
    })
  }

  const fetchCustomizationSettingsQuery = (productId: MaybeRefOrGetter<string>, params?: MaybeRefOrGetter<Record<string, unknown>>) => {
    return useQuery<Record<string, unknown>, Error>({
      queryKey: computed(() => queryKeys.customizationSettings(toValue(productId), toValue(locationId))),
      queryFn: async () => {
        const settings = await service.getCustomizationSettings(toValue(productId), toValue(productLocationId), toValue(params))
        return settings
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && !!toValue(productId)),
    })
  }

  const fetchProductTemplateByProductIdQuery = (productId: MaybeRefOrGetter<string>) => {
    return useQuery<ICourseTemplate, Error>({
      queryKey: computed(() => queryKeys.templateByProductId(toValue(productId), toValue(locationId))),
      queryFn: async () => {
        const template = await service.getProductTemplateByProductId(toValue(productId), toValue(productLocationId))
        return template as ICourseTemplate
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && !!toValue(productId)),
    })
  }

  const checkIfProductThemeExistsQuery = (productId: MaybeRefOrGetter<string>) => {
    return useQuery<Record<string, unknown>, Error>({
      queryKey: computed(() => queryKeys.themeExists(toValue(productId), toValue(locationId))),
      queryFn: async () => {
        const exists = await service.checkIfProductThemeExists(toValue(productId), toValue(productLocationId))
        return exists
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && !!toValue(productId)),
    })
  }

  const fetchProductTemplateByIdQuery = (templateId: MaybeRefOrGetter<string>, productId: MaybeRefOrGetter<string>) => {
    return useQuery<Record<string, unknown>, Error>({
      queryKey: computed(() => queryKeys.templateById(toValue(templateId), toValue(productId), toValue(locationId))),
      queryFn: async () => {
        const template = await service.getProductTemplateById(toValue(templateId), toValue(productId), toValue(productLocationId))
        return template
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && !!toValue(templateId) && !!toValue(productId)),
    })
  }

  const fetchTemplateByIdQuery = (templateId: MaybeRefOrGetter<string>, productId: MaybeRefOrGetter<string>) => {
    return useQuery<Record<string, unknown>, Error>({
      queryKey: computed(() => queryKeys.template(toValue(templateId), toValue(locationId))),
      queryFn: async () => {
        const template = await service.getTemplateById(toValue(templateId), toValue(productId), toValue(productLocationId))
        return template
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && !!toValue(templateId)),
    })
  }

  const fetchProductDataFromOfferQuery = (productId: MaybeRefOrGetter<string>) => {
    return useQuery<IProductDetail, Error>({
      queryKey: computed(() => queryKeys.offer(toValue(productId), toValue(locationId))),
      queryFn: async () => {
        const product = await offerService.getProductDataFromOffer(toValue(productId))
        return product
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && !!toValue(productId)),
    })
  }

  const fetchResolvedProductInfoQuery = (productId: MaybeRefOrGetter<string>) => {
    return useQuery<{ id: string; title: string; description: string; locationId: string } | null, Error>({
      queryKey: computed(() => queryKeys.resolve(toValue(productId), toValue(locationId))),
      queryFn: async () => {
        const locId = toValue(locationId)
        if (!locId) throw new Error('Location ID is required')
        return learnerService.getBasicProductInfo({
          courseId: toValue(productId),
          locationId: locId,
        }) as Promise<{ id: string; title: string; description: string; locationId: string }>
      },
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId) && !!toValue(productId)),
      initialData: () => {
        const locId = toValue(locationId)
        const pId = toValue(productId)
        if (!locId || !pId) return undefined
        const middlewareCacheKey = ['courses-access-guard', 'basic-product-info', locId, pId] as const
        return (
          queryClient.getQueryData<{ id: string; title: string; description: string; locationId: string }>(middlewareCacheKey) ?? undefined
        )
      },
    })
  }

  const fetchProductAccessQuery = (
    productId: MaybeRefOrGetter<string>,
    source: MaybeRefOrGetter<string>,
    queryOptions?: { enabled?: MaybeRefOrGetter<boolean> }
  ) => {
    return useQuery<CourseAccessData | null, Error>({
      queryKey: computed(() => coursesAccessGuardQueryKeys.productAccess(toValue(productId), toValue(locationId) ?? '', toValue(source))),
      queryFn: () =>
        learnerService.checkProductAccess({
          courseId: toValue(productId),
          locationId: toValue(locationId) ?? '',
          source: toValue(source),
        }) as Promise<CourseAccessData>,
      staleTime: 5 * 60 * 1000,
      enabled: computed(() => {
        const _enabled = toValue(queryOptions?.enabled)
        if (typeof _enabled === 'boolean') return _enabled
        return !!toValue(locationId) && !!toValue(productId)
      }),
    })
  }

  /** Learner's access window (expired / not-yet-started dates); never cached so a revoke is seen immediately. */
  const fetchProductValidityQuery = (productId: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean>) => {
    return useQuery<ProductValidity, Error>({
      queryKey: computed(() => queryKeys.validity(toValue(productId), toValue(locationId))),
      queryFn: () => service.getValidity(toValue(productId)),
      staleTime: 0,
      enabled: computed(() => toValue(enabled) && !!toValue(locationId) && !!toValue(productId)),
    })
  }

  const fetchCompleteProductQuery = (productId: MaybeRefOrGetter<string>, options?: CompleteProductQueryOptions) => {
    const alwaysFresh = options?.alwaysFresh ?? true
    return useQuery<ICompleteProduct, Error>({
      queryKey: computed(() => queryKeys.completeDetail(toValue(productId), toValue(locationId))),
      queryFn: async () => {
        const locId = toValue(locationId)
        if (!locId) throw new Error('Location ID is required')
        return learnerService.getProductById({ courseId: toValue(productId), locationId: locId }) as Promise<ICompleteProduct>
      },
      // InitThemeLayout never serves stale cache (theme must reflect the latest admin edits, e.g.
      // templateId); pages that only pick a layout reuse the cached product across lesson navigation.
      staleTime: alwaysFresh ? 0 : 5 * 60 * 1000,
      refetchOnMount: alwaysFresh ? 'always' : true,
      enabled: computed(() => {
        const isOptEnabled = options?.enabled !== undefined ? toValue(options.enabled) : true
        return isOptEnabled && !!toValue(locationId) && !!toValue(productId)
      }),
    })
  }

  // Mutations
  const pinProductMutation = useMutation({
    mutationFn: (productId: string) => service.pinProduct(productId),
    onSuccess: (data, productId) => {
      // Invalidate pinned products query
      queryClient.invalidateQueries({
        queryKey: queryKeys.pinned(toValue(locationId)),
      })
      // Update the specific product query if it exists
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(productId, toValue(locationId)),
      })
    },
  })

  const unpinProductMutation = useMutation({
    mutationFn: (productId: string) => service.unpinProduct(productId),
    onSuccess: (data, productId) => {
      // Invalidate pinned products query
      queryClient.invalidateQueries({
        queryKey: queryKeys.pinned(toValue(locationId)),
      })
      // Update the specific product query if it exists
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(productId, toValue(locationId)),
      })
    },
  })

  return {
    queryKeys,
    fetchProductQuery,
    fetchPinnedProductsQuery,
    fetchCustomizationSettingsQuery,
    fetchProductTemplateByProductIdQuery,
    checkIfProductThemeExistsQuery,
    fetchProductTemplateByIdQuery,
    fetchTemplateByIdQuery,
    fetchProductDataFromOfferQuery,
    fetchResolvedProductInfoQuery,
    fetchProductAccessQuery,
    fetchProductValidityQuery,
    fetchCompleteProductQuery,
    pinProductMutation,
    unpinProductMutation,
  }
}
